// ─────────────────────────────────────────────────────────────────────────────
// GENERATED FILE — DO NOT EDIT HERE.
//
// Source of truth: invits-app-new/shared/upgradePricing.js
// Regenerate:     npm run sync:shared   (from invits-app-new)
//
// Editing this copy reintroduces exactly the app/web divergence that shared/
// exists to eliminate. Change the source, then re-run the sync.
// ─────────────────────────────────────────────────────────────────────────────

// shared/upgradePricing.js
//
// What an upgrade costs, in the buyer's currency, and the GST inside it.
//
// Used by the website (to show prices) and by the payment server functions
// (to charge them - the server always recomputes; a price from the browser is
// never trusted). Pure: no imports, no I/O.
//
// ── Two kinds of buyer ─────────────────────────────────────────────────────
//
// In India: rupees, exactly as before. The tier's price plus 18% GST, split
// CGST + SGST when the buyer is in the seller's state, IGST otherwise. Prices
// are shown before tax, with the GST added at checkout, as Indian buyers expect.
//
// Outside India: the buyer's own currency, at a clean local price set per tier
// (price books below, editable in Invits Office). There is no LUT (letter of
// undertaking) on file, so the export is not zero-rated: 18% IGST applies. It
// is INCLUDED in the local price - $4.99 is what the card is charged, and the
// invoice shows the IGST inside it - because a price that grows by 18% at the
// last step is not a clean price anywhere.
//
// ── Currencies ─────────────────────────────────────────────────────────────
//
// Razorpay charges international cards in the order's currency and settles in
// rupees. Countries without a price book of their own pay in US dollars.

export const GST_RATE = 18;

export const CURRENCIES = {
  INR: { symbol: '₹', name: 'Indian rupee', locale: 'en-IN' },
  USD: { symbol: '$', name: 'US dollar', locale: 'en-US' },
  GBP: { symbol: '£', name: 'British pound', locale: 'en-GB' },
  EUR: { symbol: '€', name: 'Euro', locale: 'de-DE' },
  CAD: { symbol: 'CA$', name: 'Canadian dollar', locale: 'en-CA' },
  AUD: { symbol: 'A$', name: 'Australian dollar', locale: 'en-AU' },
  AED: { symbol: 'AED ', name: 'UAE dirham', locale: 'en-AE' },
  SGD: { symbol: 'S$', name: 'Singapore dollar', locale: 'en-SG' },
};

/** The currencies an international price book may hold, in display order. */
export const INTERNATIONAL_CURRENCIES = ['USD', 'GBP', 'EUR', 'CAD', 'AUD', 'AED', 'SGD'];

const EURO_COUNTRIES = new Set([
  'AT', 'BE', 'CY', 'DE', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'IE', 'IT',
  'LT', 'LU', 'LV', 'MT', 'NL', 'PT', 'SI', 'SK',
]);

/** ISO country code -> the currency a buyer there pays in. */
export function currencyForCountry(country) {
  const c = String(country || '').toUpperCase();
  if (c === 'IN') return 'INR';
  if (c === 'GB') return 'GBP';
  if (EURO_COUNTRIES.has(c)) return 'EUR';
  if (c === 'CA') return 'CAD';
  if (c === 'AU') return 'AUD';
  if (c === 'AE') return 'AED';
  if (c === 'SG') return 'SGD';
  return 'USD';
}

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

/** Razorpay wants the smallest unit: paise, cents. Every currency here has 2 decimals. */
export const toMinorUnits = (amount) => Math.round(Number(amount) * 100);

/** "₹235", "$4.99", "€27.99" - whole amounts without decimals. */
export function formatMoney(amount, currency) {
  const meta = CURRENCIES[currency] || { symbol: `${currency} `, locale: 'en-US' };
  const n = Number(amount) || 0;
  const whole = Math.abs(n - Math.round(n)) < 0.005;
  const text = n.toLocaleString(meta.locale, {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  });
  return `${meta.symbol}${text}`;
}

const sameState = (a, b) =>
  !!a && !!b && String(a).toLowerCase().trim() === String(b).toLowerCase().trim();

/**
 * India: base price in rupees, GST on top.
 * @returns {Quote}
 */
export function quoteIndia(baseInr, { customerState, sellerState } = {}) {
  const base = round2(baseInr);
  const gst = round2((base * GST_RATE) / 100);
  const total = round2(base + gst);
  if (sameState(customerState, sellerState)) {
    const cgst = round2(gst / 2);
    return { currency: 'INR', base, gst, cgst, sgst: round2(gst - cgst), igst: 0, total, gstType: 'intrastate', taxInclusive: false };
  }
  return { currency: 'INR', base, gst, cgst: 0, sgst: 0, igst: gst, total, gstType: 'interstate', taxInclusive: false };
}

/**
 * Outside India: the local price is the total, with 18% IGST inside it.
 * @returns {Quote}
 */
export function quoteExport(totalLocal, currency) {
  const total = round2(totalLocal);
  const base = round2(total / (1 + GST_RATE / 100));
  const igst = round2(total - base);
  return { currency, base, gst: igst, cgst: 0, sgst: 0, igst, total, gstType: 'export', taxInclusive: true };
}

/**
 * The price for one buyer.
 *
 * @param {object} p
 * @param {string} p.country        ISO code of the buyer's country
 * @param {number} p.inrBase        the rupee price before GST
 * @param {Object<string, number>} [p.localPrices]  international totals by currency
 * @param {string} [p.customerState]  Indian buyers: their state, for CGST/SGST vs IGST
 * @param {string} [p.sellerState]
 * @returns {Quote}
 * @throws when an international buyer has no price in their currency or USD
 *
 * @typedef {{ currency: string, base: number, gst: number, cgst: number, sgst: number,
 *   igst: number, total: number, gstType: 'intrastate'|'interstate'|'export', taxInclusive: boolean }} Quote
 */
export function quoteForBuyer({ country, inrBase, localPrices, customerState, sellerState }) {
  const c = String(country || 'IN').toUpperCase();
  if (c === 'IN') return quoteIndia(inrBase, { customerState, sellerState });

  const wanted = currencyForCountry(c);
  const prices = localPrices || {};
  const currency = Number(prices[wanted]) > 0 ? wanted : 'USD';
  const price = Number(prices[currency]);
  if (!(price > 0)) throw new Error(`No international price in ${wanted} or USD`);
  return quoteExport(price, currency);
}

/** Multiplies every price in a book: album storage is priced per GB per month. */
export function scalePrices(prices, factor) {
  const out = {};
  for (const [cur, v] of Object.entries(prices || {})) {
    if (Number(v) > 0) out[cur] = round2(Number(v) * factor);
  }
  return out;
}

/** Album storage: whole GB, at least 1, as the rupee price has always counted it. */
export const albumSizeGb = (bytes) => Math.max(1, Math.ceil((Number(bytes) || 0) / (1024 ** 3)));
